// Copyright ©️ 2026 eVolve MEP, LLC

import type { ArrayOptional, StrongExclude } from '../_helpers/helper-types';

import type { FacilityId } from '../Facility/types';
import type { DateOnly } from '../Misc/dateOnly';
import type { CompanyId, TimeFrameParams } from '../Misc/types';
import type { ProjectId } from '../Project/types';
import type { WorkCellId } from '../ShopSetup/types';
import type { TaskId, TaskStatusTypeId, TaskStatusTypeName } from '../Tasks/types';
import type { UserId } from '../User/types';
import type {
  WorkOrderId,
  WorkOrderItemId,
  WorkOrderStateId,
  WorkOrderStateName,
  WorkOrderStatus,
  WorkOrderStatusId,
} from '../WorkOrder/types';

/**
 * Unlike the other insights endpoints, the timedata endpoint
 * does not accept the "All (entity name)" string.
 */
export type TaskTimeDataFilterToApi = TimeFrameParams &
  ArrayOptional<{
    projectIds: ProjectId[];
    facilityIds: FacilityId[];
    workOrderStateIds: WorkOrderStateId[];
  }>;

export type DashboardFilterToApi = TimeFrameParams &
  ArrayOptional<{
    projects: (ProjectId | 'All Projects')[];
    facilities: (FacilityId | 'All Facilities')[];
    states: (WorkOrderStateId | 'All States')[];
  }>;

const chartStatuses = [
  'Draft',
  'Not Started',
  'In Progress',
  'Blocked',
  'Completed',
  'Late',
  'Canceled',
  'Submitted',
  'Pending',
  'Approved',
  'Rejected',
] as const satisfies (WorkOrderStateName | 'Late')[];

export type InsightsChartStatus = (typeof chartStatuses)[number];

type LateChartStatus = StrongExclude<InsightsChartStatus, 'Late' | 'Canceled'>;

const insightsLateStatusKeyNames: `${LateChartStatus} - Late`[] = [
  'Draft - Late',
  'Rejected - Late',
  'Submitted - Late',
  'Pending - Late',
  'Approved - Late',
  'Not Started - Late',
  'In Progress - Late',
  'Completed - Late',
  'Blocked - Late',
] as const;

export const insightsStateTypeNames = [...chartStatuses, ...insightsLateStatusKeyNames] as const;

export type InsightsStateTypeName = (typeof insightsStateTypeNames)[number];

export type ChartDataPoints = {
  Month: string;
} & { [k in InsightsStateTypeName]?: number };

export type WorkOrdersOverTimeRes = {
  completedWorkOrders: number;
  percentageChangeFromPreviousTimeFrameForCompletedWorkOrders: number;
  lateWorkOrders: number;
  percentageChangeFromPreviousTimeFrameForLateWorkOrders: number;
  percentageOfLateVersusTotalWorkOrders: number;
  averageManufacturingLeadTime: number;
  percentageChangeFromPreviousTimeFrameForAverageManufacturingLeadTime: number;
  averageProductionLeadTime: number;
  percentageChangeFromPreviousTimeFrameForAverageProductionLeadTime: number;
  chartDataPoints: ChartDataPoints[];
};

export type InsightsDetailsWorkOrder = {
  workOrderId: WorkOrderId;
  workOrderIdentifier: string;
  projectName: string;
  facilityName: string;
  workOrderName: string;
  workOrderDescription: string;
  workOrderStateName: WorkOrderStateName;
  workOrderStatusId?: WorkOrderStatusId;
  workOrderTotalTimeInSeconds?: number;
  estimatedTimeInSeconds: number;
  needByDate: DateOnly;
  onSiteDate: DateOnly;
  daysRemaining: number;
  dateApproved: DateOnly;
  dateInProgress: DateOnly;
  dateCompleted: DateOnly;
  daysEarly: number;
  daysLate: number;
  manufacturingLeadTimeDays: number;
  productionLeadTimeDays: number;
};

export type TaskData = {
  taskId: TaskId;
  projectId: ProjectId;
  facilityId: FacilityId;
  workOrderStateId: WorkOrderStateId;
  workOrderItemId: WorkOrderItemId;
  workOrderId: WorkOrderId;
  workCellId: WorkCellId;
  taskName: string;
  taskStatusTypeName: TaskStatusTypeName;
  taskStatusTypeId: TaskStatusTypeId;
  comments: string;
  workOrderIdentifier: string;
  workOrderName: string;
  projectName: string;
  facilityName: string;
  workOrderStateName: WorkOrderStateName;
  workOrderStatusName: WorkOrderStatus['name'];
  workOrderItemName: string;
  workCellName: string;
  needByDate: DateOnly;
  onSiteDate?: DateOnly;
  dateStarted?: DateOnly;
  dateCompleted: DateOnly;
  quantity: number;
  runTimeInSeconds: number;
  adjustedTimeInSeconds?: number;
  totalTimeEntriesInSeconds?: number;
  totalTimeInSeconds: number;
  numberOfAttachments: number;
};

export type VDCOSChartToolType = 'Evolve' | 'Revit';

export type EventsWeeklySummaryResp = {
  averageEventsPerWeek: number;
  totalEvents: number;
  weeklyEvents: {
    startOfWeek: DateOnly;
    totalEvents: number;
    weightForAverage: number;
  }[];
};

export type EventsPerUserResp = {
  avgEventsPerUser: number;
  totalEvents: number;
  avgEventsPerWeek: number;
  eventsPerUser: {
    email: string;
    totalEvents: number;
  }[];
};

export type FeaturesPerUserResp = {
  avgFeaturesPerUser: number;
  totalFeatures: number;
  avgFeaturesPerWeek: number;
  featuresPerUser: {
    email: string;
    totalFeatures: number;
  }[];
};

export type RevitVersion = {
  revitVersion: string;
  revitYear: string;
};

export type ProductVersion = RevitVersion & {
  email: string;
  productVersion: string;
  userId: UserId;
};

declare const featureTypeNameSymbol: unique symbol;
export type FeatureTypeName = string & { [featureTypeNameSymbol]: never };

export type EventsByFeature = {
  featureTypeName: FeatureTypeName;
  totalUsage: number;
};

export type SyncTimesResponse = {
  document: string;
  email: string;
  distinctSessionCount: number;
  distinctUserCount: number;
  totalExecutions: number;
  /** in milliseconds */
  averageExecutionTime: number;
  averageRecordsPerActiveDay: number;
  totalExecutionFailures: number;
};

export type MaterialUseConfigurationRequest = {
  documentCaptureOption: number;
  captureConduit: boolean;
  captureCableTray: boolean;
  captureDuct: boolean;
  capturePipe: boolean;
  /** A list of document names to blacklist or whitelist */
  documents?: string[];
};

declare const materialUseConfigurationIdSymbol: unique symbol;
type MaterialUseConfigurationId = string & { [materialUseConfigurationIdSymbol]: never };

export type MaterialUseConfigurationResponse = MaterialUseConfigurationRequest & {
  companyId: CompanyId;
  configurationId: MaterialUseConfigurationId;
};

export type MaterialUseConfigurationPreference = {
  value: number;
  name: string;
  description: string;
};

declare const materialUseDocumentSymbol: unique symbol;
export type MaterialUseDocument = string & { [materialUseDocumentSymbol]: never };

declare const materialUseLevelSymbol: unique symbol;
export type MaterialUseLevel = string & { [materialUseLevelSymbol]: never };

declare const materialUseWorksetSymbol: unique symbol;
export type MaterialUseWorkset = string & { [materialUseWorksetSymbol]: never };

declare const materialUseTypeSymbol: unique symbol;
export type MaterialUseType = string & { [materialUseTypeSymbol]: never };

declare const materialUseChangeByUserSymbol: unique symbol;
export type MaterialUseChangedByUser = string & { [materialUseChangeByUserSymbol]: never };

export type MaterialChartUsageGroup = 'cableTray' | 'conduit' | 'duct' | 'pipe';

export type MaterialChartPropertyGroup = 'type' | 'material' | 'level' | 'service' | 'tradeSize' | 'workset';

export type MaterialChartFilters = {
  documents?: MaterialUseDocument | MaterialUseDocument[];
  levels?: MaterialUseLevel | MaterialUseLevel[];
  worksets?: MaterialUseWorkset | MaterialUseWorkset[];
  types?: MaterialUseType | MaterialUseType[];
  lastChangers?: MaterialUseChangedByUser | MaterialUseChangedByUser[];
} & TimeFrameParams;

export type MaterialChartSinglePropertyGroupingRequest = MaterialChartFilters & {
  usageGroup: MaterialChartUsageGroup;
  primaryGrouping: MaterialChartPropertyGroup;
};

export type MaterialChartTwoPropertyGroupingRequest = MaterialChartSinglePropertyGroupingRequest & {
  secondaryGrouping: MaterialChartPropertyGroup;
};

type BaseMeasurementChartResponse<T> = {
  captureDate?: DateOnly;
  data: T[];
};

export type AggregatedMeasurementChartResponse = BaseMeasurementChartResponse<{
  primaryGrouping: string;
  secondaryGrouping: string;
  value: number;
}>;
export type DensityMeasurementChartResponse = BaseMeasurementChartResponse<{
  primaryGrouping: string;
  value: number;
}>;

type CumulativeChartResponseBase = {
  primaryGrouping: string;
  captureDate: DateOnly;
};

export type MaterialCumulativeQuantityResponse = CumulativeChartResponseBase & {
  straightCount: number;
  fittingCount: number;
  totalQuantity: number;
};

export type MaterialCumulativeLengthResponse = CumulativeChartResponseBase & {
  totalLength: number;
};
